import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Database, Eye, EyeOff, Loader2 } from "lucide-react";
import {
  AuthError,
  getCurrentUser,
  initialize as authInitialize,
  isEntra,
  login as authLogin,
  loginInteractive as authLoginInteractive,
} from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// Login dual: con provider=keycloak es un formulario embebido que habla con
// /api/v1/auth/login (Direct Access Grants); con provider=entra es un botón
// que dispara el login interactivo de Microsoft (MSAL, redirect + PKCE).
// La pantalla vive fuera del AuthProvider, así que usa la API de auth directa.

/**
 * URL del flujo "olvidé mi contraseña" de Keycloak (link directo a
 * `/login-actions/reset-credentials`, el mismo endpoint del botón
 * "Forgot password?" del login nativo). Si no hay VITE_KEYCLOAK_URL
 * configurada, el link no se muestra.
 */
function buildForgotPasswordUrl(): string | null {
  const base = (import.meta.env.VITE_KEYCLOAK_URL || "").replace(/\/+$/, "");
  if (!base) return null;
  const realm = import.meta.env.VITE_KEYCLOAK_REALM || "montevive";
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "rag-interno";
  const params = new URLSearchParams({ client_id: clientId });
  return `${base}/realms/${realm}/login-actions/reset-credentials?${params}`;
}

/** Fondo Cuerva: negro Conocimiento + asterisco Amarillo Energía. */
function CuervaBackdrop() {
  return (
    <>
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background: "linear-gradient(160deg, #130E1E 0%, #2b2734 100%)",
        }}
      />
      <div
        className="absolute -top-40 -right-40 w-[460px] h-[460px] pointer-events-none select-none font-heading"
        aria-hidden
        style={{
          color: "rgba(255, 218, 0, 0.10)",
          fontSize: "560px",
          lineHeight: 1,
        }}
      >
        *
      </div>
    </>
  );
}

export default function LoginPage() {
  return isEntra ? <EntraLogin /> : <KeycloakLogin />;
}

// --- Login con Microsoft Entra ID (MSAL) ------------------------------------

function EntraLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const callbackUrl = searchParams.get("callbackUrl") || "/proyectos";

  // Si ya hay sesión MSAL (p. ej. al volver del redirect), entra directo.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await authInitialize();
        if (!cancelled && getCurrentUser()) {
          navigate(callbackUrl, { replace: true });
        }
      } catch {
        /* sin sesión: el usuario pulsa el botón */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callbackUrl, navigate]);

  async function handleMicrosoftLogin() {
    if (submitting) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      // Redirige a Microsoft; al volver, el efecto de arriba recupera sesión.
      await authLoginInteractive();
    } catch {
      setErrorMessage(
        "No se pudo iniciar sesión con Microsoft. Inténtalo de nuevo."
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 relative overflow-hidden bg-ink">
      <CuervaBackdrop />
      <Card className="w-full max-w-sm bg-surface border-line shadow-card relative z-10">
        <CardHeader className="text-center">
          <span className="mx-auto mb-4 grid place-items-center w-12 h-12 bg-brand text-white">
            <Database className="w-6 h-6" />
          </span>
          <CardTitle className="font-heading text-2xl text-ink tracking-tightest">
            RAG Interno<span className="cuerva-asterisk ml-0.5">*</span>
          </CardTitle>
          <CardDescription className="text-content-muted">
            Inicia sesión para acceder a la plataforma
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {errorMessage && (
              <div
                className="flex items-start gap-2 rounded border-l-4 border-danger bg-danger/10 p-3 text-sm text-danger"
                role="alert"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <Button
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={submitting}
              size="lg"
              className="w-full bg-ink hover:bg-ink/90 border border-ink text-white font-heading font-semibold rounded"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitting
                ? "Redirigiendo a Microsoft…"
                : "Iniciar sesión con Microsoft"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Login embebido con Keycloak (ROPC) -------------------------------------

function KeycloakLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const callbackUrl = searchParams.get("callbackUrl") || "/proyectos";
  const forgotPasswordUrl = buildForgotPasswordUrl();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await authLogin(username.trim(), password);
      navigate(callbackUrl, { replace: true });
    } catch (err) {
      if (err instanceof AuthError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage("Error de red al iniciar sesión. Inténtalo de nuevo.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 relative overflow-hidden bg-ink">
      <CuervaBackdrop />

      <Card className="w-full max-w-sm bg-surface border-line shadow-card relative z-10">
        <CardHeader className="text-center">
          <span className="mx-auto mb-4 grid place-items-center w-12 h-12 bg-brand text-white">
            <Database className="w-6 h-6" />
          </span>
          <CardTitle className="font-heading text-2xl text-ink tracking-tightest">
            RAG Interno<span className="cuerva-asterisk ml-0.5">*</span>
          </CardTitle>
          <CardDescription className="text-content-muted">
            Inicia sesión para acceder a la plataforma
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {errorMessage && (
              <div
                className="flex items-start gap-2 rounded border-l-4 border-danger bg-danger/10 p-3 text-sm text-danger"
                role="alert"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label
                htmlFor="username"
                className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
              >
                Usuario o email
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={submitting}
                className="w-full px-3 py-2 rounded border border-line bg-surface text-sm text-content placeholder:text-content-muted focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-60"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={submitting}
                  className="w-full px-3 py-2 pr-10 rounded border border-line bg-surface text-sm text-content placeholder:text-content-muted focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 grid place-items-center px-3 text-content-muted hover:text-ink"
                  aria-label={
                    showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {forgotPasswordUrl && (
                <div className="pt-0.5 text-right">
                  <a
                    href={forgotPasswordUrl}
                    className="text-xs font-heading font-semibold text-brand hover:text-brand-hover underline-offset-2 hover:underline"
                  >
                    ¿Has olvidado tu contraseña?
                  </a>
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-brand hover:bg-brand-hover border border-brand-border text-white font-heading font-semibold rounded"
              size="lg"
              disabled={submitting || !username.trim() || !password}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitting ? "Iniciando sesión…" : "Iniciar sesión"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
